import { forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

// TimePickerPopover — click-to-open 24h time picker. Replaces the bare
// text TimeInput24h in places where the user wants a click-friendly
// picker rather than a typing field. Two scrollable hour/minute columns
// inside a popover; selected values are committed to onChange on each
// click so the parent stays in sync without an explicit "OK" button.
//
// Value contract is identical to TimeInput24h: "HH:MM" (24h) strings,
// empty means "no value yet". Stays locale-independent — no AM/PM —
// per the SportAdmin 24h convention.

interface Props {
  value: string;
  onChange: (next: string) => void;
  // Step in minutes for the minute column. Defaults to 5 — 12 visible
  // options keeps the column compact while still letting users hit
  // every common practice cadence (15/30/45 + the odd 25/40 case).
  minuteStep?: number;
  // Optional label shown next to the clock icon. When omitted the
  // value itself is the only thing inside the trigger.
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}

export function TimePickerPopover({
  value,
  onChange,
  minuteStep = 5,
  ariaLabel,
  disabled,
  className,
  id,
}: Props) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hoursColRef = useRef<HTMLDivElement>(null);
  const minsColRef = useRef<HTMLDivElement>(null);

  const [hh, mm] = parseHHMM(value);
  const display = value || '--:--';

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) => i * minuteStep);

  // Close on outside click and Escape — same pattern as the other
  // popover components in the app.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // When the popover opens, scroll the currently-selected hour and
  // minute into view so the user lands on their current value rather
  // than the top of the column.
  useEffect(() => {
    if (!open) return;
    const align = (col: HTMLDivElement | null, idx: number) => {
      if (!col) return;
      const child = col.children[idx] as HTMLElement | undefined;
      if (!child) return;
      // Center the selected row *within its own column* by setting scrollTop
      // directly. scrollIntoView({block:'center'}) also scrolls every
      // scrollable ancestor (the sheet body, the page), which yanks the whole
      // popover and leaves the highlight sitting off-center.
      col.scrollTop =
        child.offsetTop - col.offsetTop - (col.clientHeight - child.offsetHeight) / 2;
    };
    align(hoursColRef.current, hh ?? 0);
    const minuteIdx = mm !== null ? minutes.indexOf(snapToStep(mm, minuteStep)) : 0;
    align(minsColRef.current, Math.max(0, minuteIdx));
  }, [open, hh, mm, minuteStep, minutes]);

  function setHour(h: number) {
    const minute = mm !== null ? snapToStep(mm, minuteStep) : 0;
    onChange(`${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
  }
  function setMinute(min: number) {
    const hour = hh !== null ? hh : 0;
    onChange(`${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`);
  }

  return (
    <div ref={containerRef} className={cn('relative inline-block w-full', className)}>
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-label={ariaLabel ?? t('timePicker.label')}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn(
          'flex min-h-9 w-full items-center justify-between gap-2 rounded-pill border border-border bg-background px-3 py-1.5 text-sm tabular-nums transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:cursor-not-allowed disabled:opacity-60',
          value ? 'text-foreground' : 'text-muted-foreground',
          open && 'border-primary',
        )}
      >
        <span>{display}</span>
        <Clock weight="bold" className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>
      {open && (
        <div
          role="dialog"
          className="absolute left-0 right-0 z-50 mt-1 flex max-w-[16rem] gap-1 rounded-xl border border-border bg-background p-2 shadow-lg"
        >
          <Column
            ref={hoursColRef}
            ariaLabel={t('timePicker.hours')}
            options={hours}
            selected={hh}
            onPick={setHour}
            format={(n) => String(n).padStart(2, '0')}
          />
          <Column
            ref={minsColRef}
            ariaLabel={t('timePicker.minutes')}
            options={minutes}
            selected={mm !== null ? snapToStep(mm, minuteStep) : null}
            onPick={setMinute}
            format={(n) => String(n).padStart(2, '0')}
          />
        </div>
      )}
    </div>
  );
}

interface ColumnProps {
  ariaLabel: string;
  options: number[];
  selected: number | null;
  onPick: (n: number) => void;
  format: (n: number) => string;
}

const Column = forwardRef<HTMLDivElement, ColumnProps>(function Column(
  { ariaLabel, options, selected, onPick, format },
  ref,
) {
  return (
    <div
      ref={ref}
      role="listbox"
      aria-label={ariaLabel}
      className="flex h-48 flex-1 flex-col gap-0.5 overflow-y-auto p-1 [scrollbar-width:thin]"
    >
      {options.map((n) => {
        const active = selected === n;
        return (
          <button
            key={n}
            type="button"
            role="option"
            aria-selected={active}
            onClick={() => onPick(n)}
            className={cn(
              'rounded-pill px-3 py-1.5 text-center text-sm tabular-nums transition-colors',
              active
                ? 'bg-primary font-bold text-primary-foreground'
                : 'text-foreground hover:bg-muted',
            )}
          >
            {format(n)}
          </button>
        );
      })}
    </div>
  );
});

// parseHHMM accepts "HH:MM", "HH:MM:SS" or "HH:MM:SS.SSSSSS" (Postgres
// TIME ends up in this format when scanned as a string) and returns the
// hour/minute components. Returns [null, null] when the input is empty
// or unparseable so the caller can render "--:--".
function parseHHMM(s: string): [number | null, number | null] {
  if (!s) return [null, null];
  const m = s.match(/^(\d{1,2}):(\d{1,2})/);
  if (!m || m[1] === undefined || m[2] === undefined) return [null, null];
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min) || h < 0 || h > 23 || min < 0 || min > 59) {
    return [null, null];
  }
  return [h, min];
}

function snapToStep(n: number, step: number): number {
  if (step <= 0) return n;
  return Math.round(n / step) * step;
}
